
import React, { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import TimeAgo from "react-timeago";
import { FaClock, FaEdit, FaTrash } from "react-icons/fa";
import { MdKeyboardVoice, MdPhotoCamera, MdMore } from "react-icons/md";
import { PiVideoCameraFill } from "react-icons/pi";

import CreateComment from "../CreateComment";
import { useUserContext } from "../../context/AuthContext";
import Voice from "./Voice";
import Photo from "./Photo";
import Video from "./Video";
import Text from "./Text";
import EditPost from "../EditPost";
import DeletePost from "../DeletePost";
import PostInfo from "../PostInfo";
import { getMediaUrl, isSameUser } from "../../lib/utils";
import ReadMore from "../ReadMore";

const Posts = ({ posts, currentId, setCurrentId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [editPost, setEditPost] = useState(false);
  const [postType, setPostType] = useState("");
  const [postId, setPostId] = useState();

  const { authUser } = useUserContext();

  /* =========================================================
     CURRENT POST
  ========================================================= */

  const currentPost = currentId ? posts?.find((p) => p.id === currentId) : null;

  return (
    <>
      {posts?.map((post) => (
        <div key={post?.id} className="flex post-card">
          <div className="w-full border p-3 bg-white shadow-lg post-card">

            {/* =================================================
                POST HEADER
            ================================================= */}

            <div className={post?.caption?.length === 0 ? "flex justify-between mb-6" : "flex justify-between"}>
              <div className="flex">
                <Link to={`/profile/${post?.creator_id}`}>
                  <img
                    src={getMediaUrl(post?.profile_img)}
                    alt="profile"
                    className="h-10 w-10 aspect-square rounded-full object-cover"
                  />
                </Link>

                <p className="flex flex-col ml-1 text-gray-500 font-medium">
                  {post?.firstname} {post?.lastname}

                  <span className="flex items-center text-center text-sm text-gray-400">
                    <FaClock className="mr-1" />
                    <TimeAgo date={post?.created_at} />
                  </span>
                </p>
              </div>

              {/* =================================================
                  EDIT / DELETE MENU
              ================================================= */}

              {isSameUser(post?.creator_id, authUser?.id) && (
                <Menu as="div" className="relative inline-block shrink-0 text-left">
                  <MenuButton
                    aria-label="Post options"
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-transparent text-gray-400 transition-all duration-200 hover:border-purple-100 hover:bg-purple-50 hover:text-purple-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
                  >
                    <MdMore size={23} />
                  </MenuButton>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-150"
                    enterFrom="opacity-0 scale-95 -translate-y-1"
                    enterTo="opacity-100 scale-100 translate-y-0"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100 scale-100 translate-y-0"
                    leaveTo="opacity-0 scale-95 -translate-y-1"
                  >
                    <MenuItems className="absolute right-0 top-full z-50 mt-2 w-52 origin-top-right rounded-2xl border border-gray-100 bg-white p-2 shadow-[0_12px_35px_rgba(15,23,42,0.14)] focus:outline-none">
                      <div className="px-3 pb-2 pt-1">
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400">
                          Post options
                        </p>
                      </div>

                      {/* EDIT */}

                      <MenuItem>
                        {({ focus }) => (
                          <button
                            type="button"
                            onClick={() => {
                              setEditPost(true);
                              setCurrentId(post.id);
                              setPostType(post.type);
                            }}
                            className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                              focus ? "bg-purple-50" : "hover:bg-purple-50"
                            }`}
                          >
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-50 text-purple-600">
                              <FaEdit size={16} />
                            </span>

                            <span className="flex flex-col">
                              <span className="text-sm font-semibold text-gray-800">
                                Edit post
                              </span>
                              <span className="text-[11px] text-gray-400">
                                Make changes
                              </span>
                            </span>
                          </button>
                        )}
                      </MenuItem>

                      <div className="my-1.5 border-t border-gray-100" />

                      {/* DELETE */}

                      <MenuItem>
                        {({ focus }) => (
                          <button
                            type="button"
                            onClick={() => {
                              setOpenDelete(true);
                              setPostId(post?.id);
                            }}
                            className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                              focus ? "bg-red-50" : "hover:bg-red-50"
                            }`}
                          >
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500">
                              <FaTrash size={15} />
                            </span>

                            <span className="flex flex-col">
                              <span className="text-sm font-semibold text-red-600">
                                Delete post
                              </span>
                              <span className="text-[11px] text-gray-400">
                                Remove this post
                              </span>
                            </span>
                          </button>
                        )}
                      </MenuItem>
                    </MenuItems>
                  </Transition>
                </Menu>
              )}
            </div>

            {/* =================================================
                CAPTION
            ================================================= */}

            {post?.caption && (
              <p className="my-3 mx-2 text-gray-500 font-semibold text-md">
                <ReadMore text={post.caption} />
              </p>
            )}

            {/* =================================================
                POST CONTENT
            ================================================= */}

            {post?.type === "text" && (
              <Text content={post?.content} />
            )}

            {post?.type === "voice" && (
              <Voice url={post?.content} postId={post?.id} />
            )}

            {post?.type === "photo" && (
              <div className="post-card_img w-full">
                <Photo content={post?.content} />
              </div>
            )}

            {post?.type === "video" && (
              <div className="post-card_img overflow-hidden">
                <Video content={post?.content} />
              </div>
            )}

            {/* =================================================
                POST INFO
            ================================================= */}

            <div className="mt-5 flex justify-between text-gray-500">
              <PostInfo post={post} user={authUser} />
            </div>

            <div className="w-full border my-2" />

            {/* =================================================
                CREATE COMMENT
            ================================================= */}

            <div className="flex justify-between w-full mt-6">
              <img
                src={getMediaUrl(authUser?.profile_img)}
                alt="authUser"
                className="w-[2.7rem] aspect-square object-cover rounded-[50%]"
              />

              <div className="flex flex-1 border rounded-lg ml-2">
                {/* TEXT COMMENT */}

                <input
                  type="text"
                  className="w-full outline-none mx-2 px-2"
                  placeholder="Write your comment..."
                  onClick={() => {
                    setIsOpen(true);
                    setPostType("text");
                    setPostId(post?.id);
                  }}
                />

                <div className="flex items-center gap-2 mr-2">
                  {/* VOICE COMMENT */}

                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(true);
                      setPostType("voice");
                      setPostId(post?.id);
                    }}
                    className="text-gray-500 group flex items-center rounded-lg py-1.5 data-[focus]:bg-white/10"
                  >
                    <MdKeyboardVoice size={20} className="text-gray-500 cursor-pointer" />
                  </button>

                  {/* PHOTO COMMENT */}

                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(true);
                      setPostType("photo");
                      setPostId(post?.id);
                    }}
                    className="text-gray-500 group flex items-center rounded-lg py-1.5 data-[focus]:bg-white/10"
                  >
                    <MdPhotoCamera size={20} className="text-gray-500 cursor-pointer" />
                  </button>

                  {/* VIDEO COMMENT */}

                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(true);
                      setPostType("video");
                      setPostId(post?.id);
                    }}
                    className="text-gray-500 group flex items-center rounded-lg py-1.5 data-[focus]:bg-white/10"
                  >
                    <PiVideoCameraFill size={20} className="text-gray-500 cursor-pointer" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* =====================================================
          MODALS
      ===================================================== */}

      <div>
        <EditPost
          isOpen={editPost}
          isClose={() => setEditPost(false)}
          currentPost={currentPost}
        />

        <DeletePost
          isOpen={openDelete}
          isClose={() => setOpenDelete(false)}
          id={postId}
        />

        <CreateComment
          isOpen={isOpen}
          isClose={() => setIsOpen(false)}
          postType={postType}
          postId={postId}
        />
      </div>
    </>
  );
};

export default Posts;
